import React, { useMemo } from "react";

import { productAnalytics, useSoilSightCurrency, useTranslation } from "utils";

import { CHART_COLORS } from "consts";

import { Button, Card, GuidanceLink, Icon, SoilSightLoader, Spin, Typography } from "components";

import { CMComparisonTable } from "pages/app/scenario-analysis/components/CMComparisonTable";
import { ComparisonViewToggle } from "pages/app/scenario-analysis/components/ComparisonViewToggle";
import { DifferenceChart } from "pages/app/scenario-analysis/components/DifferenceChart";
import { DifferenceYearTable } from "pages/app/scenario-analysis/components/DifferenceYearTable";
import { ForecastChart } from "pages/app/scenario-analysis/components/ForecastChart";
import { ResilienceScorecard } from "pages/app/scenario-analysis/components/ResilienceScorecard";
import { SectionHeading } from "pages/app/scenario-analysis/components/SectionHeading";
import { useDifferenceView } from "pages/app/scenario-analysis/hooks/useDifferenceView";
import { useMainCropLabelLookup } from "pages/app/scenario-analysis/hooks/useMainCropOptions";
import { useOutcomeComparisonState } from "pages/app/scenario-analysis/hooks/useOutcomeComparisonState";
import { useScenarioResilienceScores } from "pages/app/scenario-analysis/hooks/useScenarioResilienceScores";
import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import {
  ForecastDatum,
  SoilSightOutcomesByScenario,
  ViewMode,
  areOutcomesStale,
  buildForecastDataFromSoilSight,
} from "pages/app/scenario-analysis/utils/forecast";
import { referenceIsReadyToCalculate } from "pages/app/scenario-analysis/utils/scenarioValidation";

type OutcomeComparisonProps = {
  scenarios: ScenarioData[];
  projectionYears: number;
  hasCalculated: boolean;
  onNavigateToReference: () => void;
  isDirty: boolean;
  onRecalculate: () => void | Promise<void>;
  isCalculating?: boolean;
  /** True while an onboarding/import auto-recalc is pending but not yet started — keeps the overlay in its spinner sub-state. */
  isAwaitingInitialCalculation?: boolean;
  /** Per-scenario CM rows from `generateSoilSightOutcomes`, keyed by `ScenarioData.id`. */
  soilSightOutcomes?: SoilSightOutcomesByScenario | null;
  marketPricePct?: number;
  costDevelopmentPct?: number;
  onEditScenario?: (scenarioId: string) => void;
  /** Opens the Detailed calculations tab; the tab itself is owned by the page. */
  onOpenCalculations?: () => void;
  /** Opens the SoilSight explainer drawer from the footer. */
  onHowIsThisCalculated?: () => void;
  /** Klim farm ID — used to fetch crop metadata for resilience score calculation. */
  klimFarmId?: string;
  /** Klim field ID — used to fetch historical crop data for accurate year-by-year resilience scores. */
  klimFieldId?: string | null;
  /** False while the connected field is missing/deleted, to keep the resilience fetch from 403-ing. */
  isFieldValid?: boolean;
  dataTestId?: string;
  /** A card's requested view, plus the scenario for views showing one; a fresh object re-applies it. */
  viewRequest?: { view: ViewMode; scenarioId?: string } | null;
  onViewRequestHandled?: () => void;
  /** The comparison scenario selected in key insights — the one the Difference view plots. */
  comparisonScenarioId?: string | null;
  /** Reports the view on show, so the key insight cards can mark the card that opens it. */
  onViewModeChange?: (view: ViewMode) => void;
};

export const OutcomeComparison = ({
  scenarios,
  projectionYears,
  hasCalculated,
  onNavigateToReference,
  isDirty,
  onRecalculate,
  isCalculating = false,
  isAwaitingInitialCalculation = false,
  soilSightOutcomes = null,
  marketPricePct = 0,
  costDevelopmentPct = 0,
  onEditScenario,
  onOpenCalculations,
  onHowIsThisCalculated,
  klimFarmId,
  klimFieldId,
  isFieldValid = true,
  dataTestId = "outcome-comparison",
  viewRequest = null,
  onViewRequestHandled,
  comparisonScenarioId = null,
  onViewModeChange,
}: OutcomeComparisonProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { currencySymbol } = useSoilSightCurrency();
  const { mainCropLabelLookup } = useMainCropLabelLookup();

  const { resilienceData, isResiliencePending, isResilienceError } = useScenarioResilienceScores({
    scenarios,
    projectionYears,
    klimFarmId,
    klimFieldId,
    isFieldValid,
  });

  const { hasDifferenceView, differenceBaseline, differenceComparison, comparisonScenarioCount, differenceSeries, differenceLineColor } = useDifferenceView({
    scenarios,
    projectionYears,
    soilSightOutcomes,
    comparisonScenarioId,
  });
  const { activeViewMode, applyViewMode, activeResilienceScenarioId, setActiveResilienceScenarioId } = useOutcomeComparisonState({
    hasDifferenceView,
    viewRequest,
    onViewRequestHandled,
    onViewModeChange,
  });

  const forecastData = useMemo<ForecastDatum[]>(
    () => buildForecastDataFromSoilSight(scenarios, projectionYears, soilSightOutcomes ?? {}),
    [scenarios, projectionYears, soilSightOutcomes]
  );

  const isReferenceReady = useMemo(() => referenceIsReadyToCalculate(scenarios), [scenarios]);

  // Shown once the reference can be forecast, and kept during a run so the progress never vanishes; staleness only dims it.
  const showResultsShell = isReferenceReady || isCalculating || isAwaitingInitialCalculation;
  const showStaleState =
    showResultsShell && areOutcomesStale({ isDirty, isCalculating, isAwaitingInitialCalculation, hasCalculated, soilSightOutcomes: soilSightOutcomes ?? null });
  const showCalculatingIndicator = isCalculating || isAwaitingInitialCalculation;

  // The breakdown holds margin figures only, so it is offered on the two margin views alone.
  const isMarginView = activeViewMode === "cumulative" || activeViewMode === "annual";
  const isDifference = activeViewMode === "difference";

  const comparisonTitle = differenceComparison?.title ?? "";
  const baselineTitle = differenceBaseline?.title ?? "";

  // The Difference view names both scenarios in its title and moves its explanation into an (i) tooltip.
  const title = isDifference
    ? t("outcomeComparison.difference.title", { scenario: comparisonTitle, baseline: baselineTitle })
    : t(`outcomeComparison.title.${activeViewMode}`);
  const tooltipTitle = isDifference
    ? t("outcomeComparison.difference.subtitle", { currency: currencySymbol, years: String(projectionYears) })
    : t(`outcomeComparison.titleTooltip.${activeViewMode}`);
  const differenceInfo = [
    t("outcomeComparison.difference.info", { scenario: comparisonTitle, baseline: baselineTitle }),
    // Only worth saying once there is a choice to have made.
    comparisonScenarioCount > 1 ? t("outcomeComparison.difference.infoOneAtATime") : null,
  ]
    .filter(Boolean)
    .join(" ");

  const viewToggle = showResultsShell ? (
    <ComparisonViewToggle
      value={activeViewMode}
      hasDifferenceView={hasDifferenceView}
      onChange={(view) => {
        // We track here, not in applyViewMode. It also runs on mount and from key insight cards.
        productAnalytics.track("soilsights:chart_view", { view_mode: view });
        applyViewMode(view);
      }}
      dataTestId={`${dataTestId}-view-mode`}
    />
  ) : null;

  return (
    <Card className="outcome-comparison" dataTestId={dataTestId} variant="outlined">
      <div className="outcome-comparison-content">
        {/* The shared heading promotes the tooltip copy to a supporting line; Difference carries its explanation in the (i). */}
        <SectionHeading
          title={title}
          supporting={tooltipTitle}
          infoLabel={isDifference ? differenceInfo : undefined}
          control={viewToggle}
          dataTestId={`${dataTestId}-heading`}
        />

        {showResultsShell ? (
          <div className="outcome-comparison-body" data-testid={`${dataTestId}-populated`}>
            <div className="outcome-comparison-stale-target-wrap">
              <div
                className={`outcome-comparison-plot-stack${showStaleState ? " outcome-comparison-plot-stack--dimmed" : ""}`}
                data-testid={`${dataTestId}-chart-table-stack`}
                aria-hidden={showStaleState || undefined}
              >
                {activeViewMode === "resilience" ? (
                  isResiliencePending ? (
                    <div className="outcome-comparison-resilience-loading" data-testid={`${dataTestId}-resilience-loading`}>
                      <Spin size="large" />
                    </div>
                  ) : isResilienceError ? (
                    <div className="outcome-comparison-resilience-error" data-testid={`${dataTestId}-resilience-error`}>
                      <Icon iconName="tabler/alert-circle" size="l" />
                      <Typography variant="paragraph-m" color="grey-600">
                        {t("resilienceScorecard.loadError")}
                      </Typography>
                    </div>
                  ) : (
                    <ResilienceScorecard
                      scenarios={scenarios}
                      resilienceData={resilienceData}
                      activeScenarioId={activeResilienceScenarioId}
                      onSelectScenario={setActiveResilienceScenarioId}
                      onEditScenario={onEditScenario}
                      scoresFresh={!showStaleState}
                      dataTestId={`${dataTestId}-resilience`}
                    />
                  )
                ) : isDifference ? (
                  <>
                    <DifferenceChart
                      series={differenceSeries}
                      comparisonTitle={comparisonTitle}
                      baselineTitle={baselineTitle}
                      lineColor={differenceLineColor}
                      dataTestId={`${dataTestId}-difference-chart`}
                    />
                    <div className="outcome-comparison-separator" aria-hidden />
                    <DifferenceYearTable
                      series={differenceSeries}
                      comparisonTitle={comparisonTitle}
                      baselineTitle={baselineTitle}
                      lineColor={differenceLineColor}
                      dataTestId={`${dataTestId}-difference-table`}
                    />
                  </>
                ) : (
                  <>
                    <ForecastChart
                      scenarios={scenarios}
                      projectionYears={projectionYears}
                      forecastData={forecastData}
                      viewMode={activeViewMode}
                      mainCropLabelLookup={mainCropLabelLookup}
                      dataTestId={`${dataTestId}-chart`}
                    />
                    <div className="outcome-comparison-separator" aria-hidden />
                    <CMComparisonTable
                      scenarios={scenarios}
                      projectionYears={projectionYears}
                      forecastData={forecastData}
                      viewMode={activeViewMode}
                      mainCropLabelLookup={mainCropLabelLookup}
                      soilSightOutcomes={soilSightOutcomes}
                      marketPricePct={marketPricePct}
                      costDevelopmentPct={costDevelopmentPct}
                      dataTestId={`${dataTestId}-table`}
                    />
                  </>
                )}
              </div>
              {showStaleState ? (
                <div className="outcome-comparison-stale-overlay" data-testid={`${dataTestId}-stale`} aria-busy={showCalculatingIndicator || undefined}>
                  <div className="outcome-comparison-stale">
                    {showCalculatingIndicator ? (
                      <SoilSightLoader size="m" className="outcome-comparison-stale-calculating" dataTestId={`${dataTestId}-stale-spin`} />
                    ) : (
                      <>
                        <div className="outcome-comparison-stale-icon" aria-hidden>
                          <Icon iconName="tabler/loader" size="l" />
                        </div>
                        <div className="outcome-comparison-stale-message">
                          <Typography variant="h5" className="outcome-comparison-stale-title">
                            {t("outcomeComparison.staleTitle")}
                          </Typography>
                          <Typography variant="paragraph-m" color="grey-600" className="outcome-comparison-stale-body">
                            {t("outcomeComparison.staleBody")}
                          </Typography>
                        </div>
                        <Button
                          type="primary"
                          size="middle"
                          icon={<Icon iconName="tabler/refresh" size="s" color="white" />}
                          iconLeft
                          onClick={onRecalculate}
                          dataTestId={`${dataTestId}-update-forecast`}
                          className="outcome-comparison-stale-cta"
                        >
                          {t("outcomeComparison.updateForecast")}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="outcome-comparison-empty" data-testid={`${dataTestId}-empty`}>
            <div className="outcome-comparison-empty-icon" aria-hidden>
              <Icon iconName="klim/crop-footprint" size={48} height={40} />
            </div>
            {/* Matches the comparison box's heading treatment, so the two blocks look like one message. */}
            <Typography variant="paragraph-m" bold className="outcome-comparison-empty-title">
              {t("outcomeComparison.emptyTitle")}
            </Typography>
            <Typography variant="paragraph-m" color="grey-600" className="outcome-comparison-empty-body">
              {/* Shares the comparison box's wording, so the page never states the same step two ways. */}
              {t("scenarioComparison.noBaselineBody")}
            </Typography>
            <Button
              type="primary"
              size="middle"
              onClick={onNavigateToReference}
              dataTestId={`${dataTestId}-complete-baseline`}
              className="outcome-comparison-empty-cta"
            >
              {t("scenarioComparison.addBaselineRotation")}
            </Button>
          </div>
        )}
      </div>

      {/* Footer strip spanning the card, matching the reference design. Withheld while results are
          stale — the overlay above carries the "Update forecast" prompt instead. */}
      {onOpenCalculations && showResultsShell && !showStaleState && isMarginView ? (
        <div className="outcome-comparison-calculations-footer">
          <Button
            type="secondary"
            size="middle"
            className="outcome-comparison-breakdown-cta"
            icon={<Icon iconName="tabler/list-check" size="m-light" color={CHART_COLORS["green-400"]} />}
            onClick={onOpenCalculations}
            dataTestId={`${dataTestId}-open-calculations`}
          >
            {t("outcomeComparison.showFinancialBreakdown")}
          </Button>
          {onHowIsThisCalculated ? (
            <GuidanceLink
              text={t("outcomeComparison.howIsThisForecastCalculated")}
              onClick={onHowIsThisCalculated}
              dataTestId={`${dataTestId}-how-is-this-calculated`}
            />
          ) : null}
        </div>
      ) : null}
    </Card>
  );
};

export default OutcomeComparison;
